import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/hooks/useWebSocket";

/**
 * Header bell + dropdown. Polls /notifications every 30s and listens on the
 * shared WebSocket for real-time pushes. WebSocket is scoped to /api/ws/messages
 * (mounted in server.py under the /api prefix so Kubernetes ingress routes it).
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user?.id]);

  // WebSocket — reuses the shared hook so we get exp-backoff reconnect.
  useWebSocket({
    enabled: !!user,
    onMessage: (msg) => {
      if (msg?.type === "message" || msg?.type === "notification") load();
    },
  });

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (_) {}
  };
  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch (_) {}
  };

  if (!user) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5 text-[#252A34]" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 max-w-[95vw] rounded-2xl border border-slate-200 bg-white shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-display font-semibold text-[#071b22]">Notifications</div>
              {unread > 0 && (
                <button onClick={markAll} className="text-xs font-medium text-[#165a71] inline-flex items-center gap-1" data-testid="notif-mark-all-read">
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-sm text-[#252A34]/60 text-center">You're all caught up.</div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { markOne(n.id); if (n.action_url) window.location.href = n.action_url; }}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${!n.read_at ? "bg-[#165a71]/5" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#165a71]" />}
                      <div className="flex-1 min-w-0">
                        <div translate={n.metadata?.lang === "es" ? "no" : undefined} className="text-sm font-medium text-[#071b22] truncate">{n.title}</div>
                        <div translate={n.metadata?.lang === "es" ? "no" : undefined} className="text-xs text-[#252A34]/70 line-clamp-2">{n.body}</div>
                        <div className="text-[10px] text-[#252A34]/50 mt-1 uppercase tracking-wider">
                          {n.event.replace(/\./g, " · ").replace(/_/g, " ")}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="p-2 border-t border-slate-100 text-center">
              <Link to="/dashboard/notifications" onClick={() => setOpen(false)} className="text-sm text-[#165a71] font-medium">
                See all notifications
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
